import React, { useCallback } from 'react'

import useFlow from 'modules/flow/state/use-flow'
import useOrder from 'modules/order/state/order/use-order'
import useStatus from 'modules/status/state/use-status'
import { StatusType } from 'modules/status/state/status-types'
import {
  trackFormSubmitEvent,
  trackFormErrorEvent,
} from 'modules/analytics/helpers/track-form-events'
import { PaymentSelectionButtonProps } from '../payment-selection-button'
import { ACHWithDonorInfoFormData } from './ach-validation'
import PaymentActionButton from '../payment-action-button'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { useTranslation } from 'next-i18next'
import { getClassySessionId } from 'modules/utils/getClassySessionId'

import useFinalizeSingleStepOrder from 'modules/order/finalize-order/use-finalize-single-step-order'
import { StructuredError } from 'types/error'
import {
  validateForms,
  saveFormsData,
} from '../../../steps/single/utils/single-step-form-orchestrator'
import { getCookieValue } from 'modules/utils/getCookieValue'

const ACHActionButton = (props: PaymentSelectionButtonProps) => {
  const flow = useFlow()
  const order = useOrder()
  const status = useStatus()
  const { t } = useTranslation()
  const finalizeSingleStepOrder = useFinalizeSingleStepOrder()

  const onNext = useCallback(async () => {
    trackFormSubmitEvent(flow.currentStep.name, { payment_type: 'ACH' })

    try {
      status.set(StatusType.SUBMITTING)

      const validationResult = await validateForms()

      if (!validationResult.success) {
        trackFormErrorEvent(
          flow.currentStep.name,
          validationResult.error || 'Form validation failed',
        )

        // Don't show toast for form validation errors - errors are shown inline
        status.set(StatusType.IDLE)
        return
      }

      // Save all form data using unified pattern
      saveFormsData(validationResult.results || [], order)

      // Get ACH-specific form data for payment details
      const achResult = validationResult.results?.find(
        (r: { formName: string }) => r.formName === 'ach',
      )

      const formData = achResult?.data as ACHWithDonorInfoFormData

      if (!formData) {
        throw new Error('ACH form data is not available')
      }

      // Set country to US for ACH if not already set (only US bank accounts work with ACH)
      if (!order.address.value.country) {
        order.address.setValue({
          ...order.address.value,
          country: 'US',
        })
      }

      // Save ACH payment details to Redux
      order.paymentDetails.setMethod({
        accountNumber: formData.accountNumber,
        routingNumber: formData.routingNumber,
      })

      // Track successful payment method selection
      if (window.newrelic) {
        window.newrelic.addPageAction('payment_method_selected', {
          pgdid: getCookieValue('pgdid'),
          'classy-session-id': getClassySessionId(),
          paymentMethod: 'ACH',
        })
      }

      await finalizeSingleStepOrder()

      status.set(StatusType.IDLE)
    } catch (e) {
      console.error(e)
      Bugsnag.notify(e as NotifiableError)

      // Now much simpler - StructuredError extends Error, so we can handle it uniformly
      const errorMessage =
        e instanceof StructuredError
          ? e // Pass the StructuredError directly
          : t(
              'achActionButton.paymentError',
              'An error has occurred while trying to finalize your payment.',
            )

      status.set(StatusType.FAILED, errorMessage)

      document
        .getElementById('ach-inputs-container')
        ?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
    }
  }, [flow, order, status, t, finalizeSingleStepOrder])

  return (
    <PaymentActionButton onClick={onNext} {...props}>
      {props.paymentMethodConfig.actionButtonText}
    </PaymentActionButton>
  )
}

export default ACHActionButton
